'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createBrowserClient } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Helper to get visitor ID (duplicated from useVisitorTracking to avoid circular deps or complex refactoring)
function getVisitorId(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('atende_visitor_id');
}

export interface SupportMessage {
    id: string;
    ticket_id: string;
    sender_id?: string | null;
    sender_type: 'customer' | 'agent';
    content: string;
    created_at: string;
    read_at: string | null;
}

export interface SupportTicket {
    id: string;
    user_id?: string | null;
    visitor_id?: string | null;
    status: 'open' | 'waiting' | 'closed';
    subject: string | null;
    created_at: string;
    last_message_at: string;
}

export function useSupportChat() {
    const [ticket, setTicket] = useState<SupportTicket | null>(null);
    const [messages, setMessages] = useState<SupportMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const supabase = createBrowserClient();
    const channelRef = useRef<RealtimeChannel | null>(null);

    const loadActiveTicket = useCallback(async () => {
        setLoading(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const visitorId = getVisitorId();

            if (!user && !visitorId) return;

            // Build query for most recent open/waiting ticket
            let query = supabase
                .from('support_tickets')
                .select('*')
                .in('status', ['open', 'waiting'])
                .order('last_message_at', { ascending: false })
                .limit(1);

            if (user) {
                query = query.eq('user_id', user.id);
            } else if (visitorId) {
                query = query.eq('visitor_id', visitorId);
            }

            const { data, error: fetchError } = await query;

            if (fetchError) throw fetchError;

            if (data && data.length > 0) {
                const activeTicket = data[0] as any;
                setTicket(activeTicket as SupportTicket);
                await loadMessages(activeTicket.id);
            } else {
                setTicket(null);
                setMessages([]);
            }
        } catch (err) {
            console.error('Error loading support ticket:', err);
            setError('Erro ao carregar chat de suporte');
        } finally {
            setLoading(false);
        }
    }, [supabase]);

    const loadMessages = useCallback(async (ticketId: string) => {
        try {
            const { data, error: fetchError } = await (supabase
                .from('support_messages') as any)
                .select('*')
                .eq('ticket_id', ticketId)
                .order('created_at', { ascending: true });

            if (fetchError) throw fetchError;
            setMessages(data || []);
        } catch (err) {
            console.error('Error loading messages:', err);
        }
    }, [supabase]);

    const startTicket = async (subject: string = 'Suporte Geral') => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const visitorId = getVisitorId();

            if (!user && !visitorId) throw new Error('Identificação não encontrada');

            const payload: any = {
                subject,
                status: 'open'
            };

            if (user) {
                payload.user_id = user.id;
            } else if (visitorId) {
                payload.visitor_id = visitorId;
            }

            const { data, error: insertError } = await (supabase
                .from('support_tickets') as any)
                .insert(payload)
                .select()
                .single();

            if (insertError) throw insertError;

            const newTicket = data as SupportTicket;
            setTicket(newTicket);
            return newTicket;
        } catch (err) {
            console.error('Error starting ticket:', err);
            throw err;
        }
    };

    const sendMessage = async (content: string) => {
        if (!content.trim()) return;
        setSending(true);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            // Don't require user, just visitor_id if needed

            let currentTicket = ticket;
            if (!currentTicket) {
                currentTicket = await startTicket();
            }

            const payload: any = {
                ticket_id: currentTicket.id,
                sender_type: 'customer',
                content: content.trim()
            };

            if (user) {
                payload.sender_id = user.id;
            }

            const { error: sendError } = await (supabase
                .from('support_messages') as any)
                .insert(payload);

            if (sendError) throw sendError;

            // Update ticket last_message_at
            await (supabase
                .from('support_tickets') as any)
                .update({ last_message_at: new Date().toISOString() })
                .eq('id', currentTicket.id);

        } catch (err) {
            console.error('Error sending message:', err);
            setError('Erro ao enviar mensagem');
        } finally {
            setSending(false);
        }
    };

    // Real-time subscription
    useEffect(() => {
        if (!ticket?.id) return;

        channelRef.current = supabase
            .channel(`support_chat_${ticket.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'support_messages',
                    filter: `ticket_id=eq.${ticket.id}`,
                },
                (payload) => {
                    setMessages(prev => {
                        if (prev.some(m => m.id === payload.new.id)) return prev;
                        return [...prev, payload.new as SupportMessage];
                    });
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'support_tickets',
                    filter: `id=eq.${ticket.id}`,
                },
                (payload) => {
                    setTicket(payload.new as SupportTicket);
                }
            )
            .subscribe();

        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
            }
        };
    }, [ticket?.id, supabase]);

    useEffect(() => {
        loadActiveTicket();
    }, [loadActiveTicket]);

    return {
        ticket,
        messages,
        loading,
        sending,
        error,
        sendMessage,
        startTicket,
        refresh: loadActiveTicket
    };
}
